<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import WootModal from 'dashboard/components/Modal.vue';

const props = defineProps({
  visible: { type: Boolean, required: true },
  action: { type: String, default: null },
  appointment: { type: Object, default: null },
});
const emit = defineEmits(['confirm', 'cancel']);
const { t } = useI18n();

const note = ref('');

watch(
  () => props.visible,
  v => {
    if (v) note.value = '';
  }
);

const config = computed(() => {
  switch (props.action) {
    case 'confirm':
      return {
        icon: '✓',
        iconBg: 'bg-n-teal-3 text-n-teal-12',
        title: 'PAINEL.CONFIRMATION_MODAL.CONFIRM_TITLE',
        descKey: 'PAINEL.CONFIRMATION_MODAL.CONFIRM_DESC',
        button: 'PAINEL.CONFIRMATION_MODAL.CONFIRM_BUTTON',
        buttonClass: 'bg-n-teal-9 text-white hover:bg-n-teal-10',
        noteLabel: 'PAINEL.CONFIRMATION_MODAL.NOTE_LABEL_OBSERVATION',
      };
    case 'cancel':
      return {
        icon: '!',
        iconBg: 'bg-n-ruby-3 text-n-ruby-12',
        title: 'PAINEL.CONFIRMATION_MODAL.CANCEL_TITLE',
        descKey: 'PAINEL.CONFIRMATION_MODAL.CANCEL_DESC',
        button: 'PAINEL.CONFIRMATION_MODAL.CANCEL_BUTTON',
        buttonClass: 'bg-n-ruby-9 text-white hover:bg-n-ruby-10',
        noteLabel: 'PAINEL.CONFIRMATION_MODAL.NOTE_LABEL_REASON',
      };
    case 'reschedule':
      return {
        icon: '⟳',
        iconBg: 'bg-n-amber-3 text-n-amber-12',
        title: 'PAINEL.CONFIRMATION_MODAL.RESCHEDULE_TITLE',
        descKey: 'PAINEL.CONFIRMATION_MODAL.RESCHEDULE_DESC',
        button: 'PAINEL.CONFIRMATION_MODAL.RESCHEDULE_BUTTON',
        buttonClass: 'bg-n-amber-9 text-white hover:bg-n-amber-10',
        noteLabel: 'PAINEL.CONFIRMATION_MODAL.NOTE_LABEL_REASON',
      };
    case 'link':
      return {
        icon: '⇆',
        iconBg: 'bg-n-blue-3 text-n-blue-12',
        title: 'PAINEL.CONFIRMATION_MODAL.LINK_TITLE',
        descKey: 'PAINEL.CONFIRMATION_MODAL.LINK_DESC',
        button: 'PAINEL.CONFIRMATION_MODAL.LINK_BUTTON',
        buttonClass: 'bg-n-blue-9 text-white hover:bg-n-blue-10',
        noteLabel: 'PAINEL.CONFIRMATION_MODAL.NOTE_LABEL_OBSERVATION',
      };
    case 'reset':
    default:
      return {
        icon: '↺',
        iconBg: 'bg-n-slate-3 text-n-slate-12',
        title: 'PAINEL.CONFIRMATION_MODAL.RESET_TITLE',
        descKey: 'PAINEL.CONFIRMATION_MODAL.RESET_DESC',
        button: 'PAINEL.CONFIRMATION_MODAL.RESET_BUTTON',
        buttonClass: 'bg-n-slate-9 text-white hover:bg-n-slate-10',
        noteLabel: 'PAINEL.CONFIRMATION_MODAL.NOTE_LABEL_OBSERVATION',
      };
  }
});

const formatHour = iso =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const formatDate = iso => new Date(iso).toLocaleDateString();

const descriptionParams = computed(() => ({
  patient: props.appointment?.contact?.name || props.appointment?.title || '—',
  time: props.appointment?.starts_at
    ? formatHour(props.appointment.starts_at)
    : '',
  date: props.appointment?.starts_at
    ? formatDate(props.appointment.starts_at)
    : '',
}));

const show = computed({
  get: () => props.visible,
  set: () => emit('cancel'),
});

const onConfirm = () => emit('confirm', note.value);
const onCancel = () => emit('cancel');
</script>

<template>
  <WootModal
    v-model:show="show"
    show-close-button
    size="modal-small"
    :on-close="onCancel"
  >
    <div class="p-6">
      <div class="flex items-start gap-4">
        <div
          class="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-lg font-bold"
          :class="config.iconBg"
        >
          {{ config.icon }}
        </div>
        <div>
          <div class="text-base font-semibold text-n-slate-12">
            {{ t(config.title) }}
          </div>
          <div class="mt-1 text-sm text-n-slate-11">
            {{ t(config.descKey, descriptionParams) }}
          </div>
        </div>
      </div>

      <label class="mt-4 block">
        <span class="text-xs uppercase text-n-slate-11">{{
          t(config.noteLabel)
        }}</span>
        <textarea
          v-model="note"
          maxlength="2000"
          rows="3"
          class="mt-1 w-full rounded border border-n-strong bg-n-surface-1 p-2 text-sm"
        />
      </label>

      <div class="mt-4 flex justify-end gap-2">
        <button
          type="button"
          class="rounded bg-n-slate-3 px-4 py-2 text-sm"
          @click="onCancel"
        >
          {{ t('PAINEL.CONFIRMATION_MODAL.BACK') }}
        </button>
        <button
          type="button"
          class="rounded px-4 py-2 text-sm font-semibold"
          :class="config.buttonClass"
          @click="onConfirm"
        >
          {{ t(config.button) }}
        </button>
      </div>
    </div>
  </WootModal>
</template>
